import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { Box, CircularProgress } from '@mui/material';

/**
 * VideoPlayer component that handles different stream types:
 * - hls: HLS video stream (uses hls.js)
 * - mjpeg: Motion JPEG stream (uses img tag)
 * - jpeg: Snapshot refresh (uses img tag with periodic refresh)
 */
function VideoPlayer({
  streamUrl,
  poster,
  streamType = 'hls',
  autoPlay = true,
  muted = true,
  refreshInterval = 1000, // For jpeg snapshot refresh
  onFatalError, // Optional callback when an unrecoverable error occurs
}) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const imgRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cleanup function
  const cleanup = () => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  useEffect(() => {
    cleanup();
    setLoading(true);
    setError(null);

    if (!streamUrl) {
      setLoading(false);
      return;
    }

    // Handle HLS streams
    if (streamType === 'hls') {
      const video = videoRef.current;
      if (!video) return;

      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 30,
        });

        hls.loadSource(streamUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setLoading(false);
          if (autoPlay) {
            video.play().catch(() => {});
          }
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            console.error('[CANVMS/VideoPlayer] HLS fatal error:', data);
            setError('Stream error');
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                hls.destroy();
                onFatalError?.();
                break;
            }
          }
        });

        hlsRef.current = hls;
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        video.src = streamUrl;
        video.onloadeddata = () => setLoading(false);
        video.onerror = () => setError('Stream error');
        if (autoPlay) {
          video.play().catch(() => {});
        }
      }
    }

    // Handle MJPEG streams (continuous image stream)
    if (streamType === 'mjpeg') {
      const img = imgRef.current;
      if (img) {
        img.onload = () => setLoading(false);
        img.onerror = () => {
          setError('Stream error');
          setLoading(false);
        };
        img.src = streamUrl;
      }
    }

    // Handle JPEG snapshot refresh
    if (streamType === 'jpeg') {
      const img = imgRef.current;
      if (img) {
        const loadImage = () => {
          // Add timestamp to bust cache
          const url = `${streamUrl}${streamUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
          img.src = url;
        };

        img.onload = () => setLoading(false);
        img.onerror = () => {
          setError('Snapshot error');
          setLoading(false);
        };

        // Initial load
        loadImage();

        // Set up refresh interval
        refreshTimerRef.current = setInterval(loadImage, refreshInterval);
      }
    }

    return cleanup;
  }, [streamUrl, streamType, autoPlay, refreshInterval]);

  // Render HLS video player
  if (streamType === 'hls') {
    return (
      <Box sx={{ width: '100%', height: '100%', bgcolor: 'black', position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <video
          ref={videoRef}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          poster={poster}
          muted={muted}
          playsInline
          controls
        />
      </Box>
    );
  }

  // Render MJPEG or JPEG image stream
  return (
    <Box sx={{ width: '100%', height: '100%', bgcolor: 'black', position: 'relative' }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}
      <img
        ref={imgRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: loading ? 'none' : 'block',
        }}
        alt="Camera feed"
      />
      {error && !loading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'error.main',
          }}
        >
          {error}
        </Box>
      )}
    </Box>
  );
}

export default VideoPlayer;
